import { cn } from '@/lib/utils'
import { Check, Copy } from 'lucide-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

const PREVIEW_ID = 'table-text-preview'
const OPEN_DELAY_MS = 400
const CLOSE_DELAY_MS = 140
const VIEWPORT_GAP = 12
const PANEL_GAP = 8
const MAX_PANEL_WIDTH = 480
const MIN_PANEL_WIDTH = 320
/** Long values scroll inside the panel instead of covering the page. */
const MAX_PANEL_HEIGHT = 360
const ARROW_SIZE = 10
const ARROW_EDGE = 16

const TARGET_SELECTOR = '[data-slot="table-cell"].text-pre-wrap-break, [data-slot="table-cell"] .text-pre-wrap-break'

type AnchorBox = {
  text: string
  label: string | null
  top: number
  right: number
  bottom: number
  left: number
  width: number
}

type Placement = {
  top: number
  left: number
  width: number
  maxHeight: number
  side: 'top' | 'bottom'
  /** Arrow offset from the panel's left edge, pointing at the cell's centre. */
  arrowLeft: number
}

const findTarget = (node: EventTarget | null) => {
  if (!(node instanceof Element)) return null
  const el = node.closest(TARGET_SELECTOR)
  return el instanceof HTMLElement ? el : null
}

const sameCell = (node: EventTarget | null, el: HTMLElement) => {
  if (!(node instanceof Element)) return false
  return node.closest('[data-slot="table-cell"]') === el.closest('[data-slot="table-cell"]')
}

const clampValue = (el: HTMLElement) => {
  const computed = getComputedStyle(el)
  return computed.getPropertyValue('-webkit-line-clamp').trim() || computed.getPropertyValue('line-clamp').trim()
}

const isClamped = (el: HTMLElement) => {
  const value = clampValue(el)
  return value !== '' && value !== 'none' && value !== '0'
}

/** Header text of the column the cell sits in, used as the preview title. */
const columnLabel = (el: HTMLElement) => {
  const cell = el.closest('td, th')
  if (!(cell instanceof HTMLTableCellElement)) return null
  const head = cell.closest('table')?.tHead
  const headerRow = head?.rows[head.rows.length - 1]
  if (!headerRow) return null

  let column = 0
  for (const sibling of Array.from(cell.parentElement?.children ?? [])) {
    if (sibling === cell) break
    column += sibling instanceof HTMLTableCellElement ? sibling.colSpan : 1
  }
  let offset = 0
  for (const th of Array.from(headerRow.cells)) {
    if (column < offset + th.colSpan) {
      const label = (th.textContent ?? '').replace(/\s+/g, ' ').trim()
      return label && label.length <= 48 ? label : null
    }
    offset += th.colSpan
  }
  return null
}

/**
 * Line-clamp reports the clipped height as scrollHeight, so overflow is measured
 * from an unclamped copy. The copy's text is read with textContent because the
 * clipped node can omit the hidden part.
 */
const measureTarget = (el: HTMLElement) => {
  if (!isClamped(el)) return null
  const rect = el.getBoundingClientRect()
  if (rect.width < 1) return null

  const computed = getComputedStyle(el)
  const clone = el.cloneNode(true) as HTMLElement
  clone.style.position = 'fixed'
  clone.style.left = '0'
  clone.style.top = '0'
  clone.style.visibility = 'hidden'
  clone.style.pointerEvents = 'none'
  clone.style.width = `${rect.width}px`
  clone.style.maxWidth = `${rect.width}px`
  clone.style.height = 'auto'
  clone.style.maxHeight = 'none'
  clone.style.overflow = 'visible'
  clone.style.display = 'block'
  clone.style.whiteSpace = 'pre-line'
  clone.style.wordBreak = 'break-word'
  clone.style.fontSize = computed.fontSize
  clone.style.fontFamily = computed.fontFamily
  clone.style.fontWeight = computed.fontWeight
  clone.style.fontStyle = computed.fontStyle
  clone.style.lineHeight = computed.lineHeight
  clone.style.letterSpacing = computed.letterSpacing
  clone.setAttribute('aria-hidden', 'true')
  document.body.appendChild(clone)

  const text = (clone.textContent ?? '').replace(/[ \t]+\n/g, '\n').trim()
  const fullHeight = clone.getBoundingClientRect().height
  clone.remove()

  if (!text || fullHeight <= rect.height + 1) return null
  return {
    text,
    label: columnLabel(el),
    top: rect.top,
    right: rect.right,
    bottom: rect.bottom,
    left: rect.left,
    width: rect.width,
  } satisfies AnchorBox
}

const placePreview = (anchor: AnchorBox, naturalHeight: number, width: number): Placement => {
  const roomBelow = window.innerHeight - anchor.bottom - PANEL_GAP - VIEWPORT_GAP
  const roomAbove = anchor.top - PANEL_GAP - VIEWPORT_GAP
  const wanted = Math.min(naturalHeight, MAX_PANEL_HEIGHT)

  let side: Placement['side'] = 'bottom'
  if (roomBelow < wanted && roomAbove > roomBelow) side = 'top'
  const room = Math.max(120, side === 'bottom' ? roomBelow : roomAbove)
  const maxHeight = Math.min(MAX_PANEL_HEIGHT, room)
  const height = Math.min(naturalHeight, maxHeight)
  const top = side === 'bottom' ? anchor.bottom + PANEL_GAP : anchor.top - PANEL_GAP - height

  const left = Math.min(
    Math.max(VIEWPORT_GAP, anchor.left),
    Math.max(VIEWPORT_GAP, window.innerWidth - width - VIEWPORT_GAP)
  )
  const anchorCentre = anchor.left + Math.min(anchor.width, width) / 2
  const arrowLeft = Math.min(Math.max(anchorCentre - left, ARROW_EDGE), width - ARROW_EDGE) - ARROW_SIZE / 2

  return { top, left, width, maxHeight, side, arrowLeft }
}

const panelWidth = (anchor: AnchorBox) =>
  Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, anchor.width), window.innerWidth - VIEWPORT_GAP * 2)

const TableTextPreview = () => {
  const hideRef = useRef<() => void>(() => {})
  const panelRef = useRef<HTMLDivElement>(null)
  const openTimerRef = useRef<number | null>(null)
  const closeTimerRef = useRef<number | null>(null)
  const anchorElRef = useRef<HTMLElement | null>(null)
  const pendingElRef = useRef<HTMLElement | null>(null)
  const [anchor, setAnchor] = useState<AnchorBox | null>(null)
  const [placement, setPlacement] = useState<Placement | null>(null)
  const [copied, setCopied] = useState(false)

  const clearOpenTimer = () => {
    if (openTimerRef.current != null) window.clearTimeout(openTimerRef.current)
    openTimerRef.current = null
  }

  const clearCloseTimer = () => {
    if (closeTimerRef.current != null) window.clearTimeout(closeTimerRef.current)
    closeTimerRef.current = null
  }

  hideRef.current = () => {
    clearOpenTimer()
    clearCloseTimer()
    pendingElRef.current = null
    anchorElRef.current = null
    setAnchor(null)
    setPlacement(null)
  }

  const scheduleClose = () => {
    clearCloseTimer()
    closeTimerRef.current = window.setTimeout(() => {
      const currentAnchor = anchorElRef.current
      const panel = panelRef.current
      if (currentAnchor?.matches(':hover') || panel?.matches(':hover')) return
      hideRef.current()
    }, CLOSE_DELAY_MS)
  }

  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!anchor || !panel) return

    setCopied(false)
    const width = panelWidth(anchor)
    panel.style.width = `${width}px`
    panel.style.maxHeight = 'none'
    setPlacement(placePreview(anchor, panel.scrollHeight, width))
  }, [anchor])

  useEffect(() => {
    const el = anchorElRef.current
    if (!anchor || !el) return
    const previous = el.getAttribute('aria-describedby')
    el.setAttribute('aria-describedby', PREVIEW_ID)
    return () => {
      if (previous) el.setAttribute('aria-describedby', previous)
      else el.removeAttribute('aria-describedby')
    }
  }, [anchor])

  useEffect(() => {
    const show = (el: HTMLElement) => {
      if (!el.isConnected || !el.matches(':hover')) return
      const next = measureTarget(el)
      if (!next) {
        if (anchorElRef.current && anchorElRef.current !== el) hideRef.current()
        return
      }
      anchorElRef.current = el
      setPlacement(null)
      setAnchor(next)
    }

    const onOver = (event: PointerEvent) => {
      if (panelRef.current?.contains(event.target as Node)) {
        clearCloseTimer()
        return
      }

      const el = findTarget(event.target)
      if (!el) return
      if (el === anchorElRef.current || el === pendingElRef.current) {
        clearCloseTimer()
        return
      }

      clearCloseTimer()
      clearOpenTimer()
      pendingElRef.current = el
      const delay = anchorElRef.current ? 0 : OPEN_DELAY_MS
      openTimerRef.current = window.setTimeout(() => {
        openTimerRef.current = null
        if (pendingElRef.current === el) pendingElRef.current = null
        show(el)
      }, delay)
    }

    const isInsideTrackedCell = (node: EventTarget | null, el: HTMLElement) =>
      node instanceof Node && (node === el || el.contains(node) || sameCell(node, el))

    const onOut = (event: PointerEvent) => {
      const next = event.relatedTarget
      if (next instanceof Node && panelRef.current?.contains(next)) {
        clearCloseTimer()
        return
      }

      const pendingEl = pendingElRef.current
      if (pendingEl && isInsideTrackedCell(event.target, pendingEl) && !isInsideTrackedCell(next, pendingEl)) {
        clearOpenTimer()
        pendingElRef.current = null
      }

      const anchorEl = anchorElRef.current
      if (anchorEl && isInsideTrackedCell(event.target, anchorEl) && !isInsideTrackedCell(next, anchorEl))
        scheduleClose()
    }

    const onPointerDown = (event: PointerEvent) => {
      if (panelRef.current?.contains(event.target as Node)) return
      hideRef.current()
    }

    const onScroll = (event: Event) => {
      if (panelRef.current && event.target instanceof Node && panelRef.current.contains(event.target)) return
      hideRef.current()
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') hideRef.current()
    }

    document.addEventListener('pointerover', onOver)
    document.addEventListener('pointerout', onOut)
    document.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('pointerover', onOver)
      document.removeEventListener('pointerout', onOut)
      document.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
      document.removeEventListener('keydown', onKeyDown)
      clearOpenTimer()
      clearCloseTimer()
    }
  }, [])

  if (!anchor) return null

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(anchor.text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      setCopied(false)
    }
  }

  const side = placement?.side ?? 'bottom'

  return createPortal(
    <div
      key={`${anchor.top}:${anchor.left}`}
      ref={panelRef}
      id={PREVIEW_ID}
      role='tooltip'
      data-side={side}
      style={{
        top: placement?.top ?? anchor.bottom + PANEL_GAP,
        left: placement?.left ?? anchor.left,
        width: placement?.width ?? panelWidth(anchor),
        maxHeight: placement?.maxHeight,
        visibility: placement ? 'visible' : 'hidden',
        pointerEvents: 'auto',
      }}
      className={cn(
        'fixed z-[100000] flex flex-col rounded-lg border border-border bg-popover text-left text-popover-foreground shadow-md ring-black/5 dark:ring-white/5',
        placement &&
          'animate-in fade-in-0 zoom-in-95 duration-150 data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1',
        side === 'bottom' ? 'origin-top-left' : 'origin-bottom-left'
      )}
      onPointerEnter={clearCloseTimer}
      onPointerLeave={scheduleClose}
    >
      <span
        aria-hidden='true'
        style={{ left: placement?.arrowLeft ?? ARROW_EDGE, width: ARROW_SIZE, height: ARROW_SIZE }}
        className={cn(
          'absolute rotate-45 border-border bg-popover',
          side === 'bottom' ? '-top-[6px] border-t border-l' : '-bottom-[6px] border-r border-b'
        )}
      />

      <div className='flex shrink-0 items-center justify-between gap-2 border-b border-border/70 py-1.5 pr-1.5 pl-3.5'>
        <span className='truncate text-[11px] font-semibold tracking-wide text-muted-foreground uppercase'>
          {anchor.label ?? 'Full text'}
        </span>
        <button
          type='button'
          onClick={copyText}
          aria-label={copied ? 'Copied' : 'Copy text'}
          className='inline-flex h-6 shrink-0 items-center gap-1 rounded-md px-1.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none'
        >
          {copied ? <Check className='size-3.5 text-emerald-600' /> : <Copy className='size-3.5' />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <div className='min-h-0 overflow-y-auto overscroll-contain px-3.5 py-2.5 text-[13px] leading-6 break-words whitespace-pre-line [scrollbar-width:thin]'>
        {anchor.text}
      </div>
    </div>,
    document.body
  )
}

export default TableTextPreview
